'use client';

import { AlertCircle, AlertTriangle, Check, Copy, Loader2 } from 'lucide-react';
import { useState } from 'react';

import { BaseModal } from '@/components/ui/BaseModal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/hooks/useToast';
import { useCreateCIToken } from '@/lib/query/useCreateCIToken';

interface CreateCITokenModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Expiry choices map to expiresInDays; '0' means never expires.
const EXPIRY_OPTIONS = [
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
  { value: '365', label: '1 year' },
  { value: '0', label: 'Never' },
];

export function CreateCITokenModal({
  isOpen,
  onClose,
}: CreateCITokenModalProps) {
  const [name, setName] = useState('');
  const [expiry, setExpiry] = useState('90');
  const [error, setError] = useState<string | null>(null);
  const [createdToken, setCreatedToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const createMutation = useCreateCIToken();

  const handleClose = () => {
    setName('');
    setExpiry('90');
    setError(null);
    setCreatedToken(null);
    setCopied(false);
    createMutation.reset();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('A token name is required');
      return;
    }

    try {
      const result = await createMutation.mutateAsync({
        name: name.trim(),
        expiresInDays: Number(expiry),
      });
      setCreatedToken(result.token);
    } catch (err) {
      const e = err as { message?: string };
      setError(e.message || 'Failed to create token. Please try again.');
    }
  };

  const handleCopy = async () => {
    if (!createdToken) return;
    try {
      await navigator.clipboard.writeText(createdToken);
      setCopied(true);
      toast.success('Copied', 'CI token copied to clipboard');
    } catch {
      toast.error('Copy failed', 'Copy the token manually');
    }
  };

  // Post-creation: show the raw token exactly once.
  if (createdToken) {
    return (
      <BaseModal
        isOpen={isOpen}
        onClose={handleClose}
        title="CI token created"
        size="sm"
      >
        <div className="space-y-4">
          <div className="bg-destructive/10 flex items-start gap-3 rounded-md p-4">
            <AlertTriangle className="text-destructive mt-0.5 h-5 w-5 flex-shrink-0" />
            <p className="text-sm">
              Copy this token now. For your security it is shown only once and
              cannot be retrieved later.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <code className="bg-muted flex-1 overflow-x-auto rounded px-3 py-2 font-mono text-sm">
              {createdToken}
            </code>
            <Button type="button" variant="outline" onClick={handleCopy}>
              {copied ? (
                <Check className="h-4 w-4" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
          </div>
          <p className="text-muted-foreground text-sm">
            Use it as the <code>MIND_CI_TOKEN</code> environment variable, or
            run <code>mind login --token</code> for headless runs.
          </p>
          <div className="flex justify-end pt-2">
            <Button type="button" onClick={handleClose}>
              Done
            </Button>
          </div>
        </div>
      </BaseModal>
    );
  }

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Create CI token"
      size="sm"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="ci-token-name">Name</Label>
          <Input
            id="ci-token-name"
            placeholder="github-actions"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={createMutation.isPending}
            aria-describedby={error ? 'ci-token-error' : undefined}
          />
          <p className="text-muted-foreground text-sm">
            A label to recognize where this token is used.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="ci-token-expiry">Expires</Label>
          <Select
            value={expiry}
            onValueChange={setExpiry}
            disabled={createMutation.isPending}
          >
            <SelectTrigger id="ci-token-expiry" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EXPIRY_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {error && (
          <div
            id="ci-token-error"
            className="bg-destructive/10 text-destructive flex items-start gap-2 rounded-md p-3 text-sm"
            role="alert"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <p>{error}</p>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={createMutation.isPending}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={createMutation.isPending}>
            {createMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              'Create token'
            )}
          </Button>
        </div>
      </form>
    </BaseModal>
  );
}
